import { useEffect, useMemo, useState } from "react"
import { IPrintingsFinalExaminations } from './types'
import { IPrintDiplomasList } from '@api/printingsDiplomas/state.types'
import { useDeviceType } from '@hooks/useDeviceType'
import { ComponentsHeader } from '@primitive/sectionHeadings'
import { Collapse } from 'antd'

import imgEmty from '@img/empty.svg'
import logoModSkill from '@img/logo_modskill.svg'
import logoYaSkill from '@img/logo_yaskill.svg'
import { IconContracts, IconDelete, IconListeners, IconPrint } from '@img/icons'

import { Button, Empty, Flex, Loader, Pagination, Table, ToolTip } from '@ui/index'

import css from './ui.module.scss'


/** Парсинг даты из строки DD.MM.YYYY */
const parseDate = (dateStr: string): Date | null => {
    if (!dateStr || dateStr.length !== 10) return null
    const [day, month, year] = dateStr.split('.')
    const date = new Date(Number(year), Number(month) - 1, Number(day))
    return isNaN(date.getTime()) ? null : date
}

/** Номер недели ISO 8601 */
const getWeekKey = (date: Date): string => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
    const dayNum = d.getUTCDay() || 7
    d.setUTCDate(d.getUTCDate() + 4 - dayNum)
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
    const weekNum = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7)
    return `${d.getUTCFullYear()}-W${String(weekNum).padStart(2, '0')}`
}

/** Пн и Пт недели по её ключу */
const getWeekRange = (weekKey: string): { monday: Date; friday: Date } | null => {
    const [, weekStr] = weekKey.split('-W')
    const week = parseInt(weekStr)
    if (isNaN(week)) return null
    const jan4 = new Date(parseInt(weekKey), 0, 4)
    const monday = new Date(jan4)
    monday.setDate(jan4.getDate() - (jan4.getDay() || 7) + 1 + (week - 1) * 7)
    const friday = new Date(monday)
    friday.setDate(monday.getDate() + 4)
    return { monday, friday }
}

/** Форматирование даты для заголовка */
const formatDateShort = (date: Date): string => {
    const months = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']
    const weekdays = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']
    return `${weekdays[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`
}

const getSiteLogo = (dataBase: string) => {
    if (dataBase === 'yaskill') return { src: logoYaSkill, alt: 'Логотип YaSkill' }
    if (dataBase === 'modern_academy') return { src: logoModSkill, alt: 'Логотип ModSkill' }
    return null
}


/** UI - Страница работ на печать */
export const PrintingsFinalExaminationsPageUI = ({
    isLoading, loadingTitle, printingsList, txtSearch, currentPageNumber, pageSize, totalCount,
    txtSearchOnChange, loadDataListeners, handleOpenCardPrinting,
    handlerOpenModalConfirm, handleOpenCardContract, handleOpenCardListener,
}: IPrintingsFinalExaminations) => {
    const deviceType = useDeviceType()
    const actionIconSize = deviceType === 'mobile' ? '16px' : '20px'

    /** Группировка списка по неделям */
    const weekGroups = useMemo(() => {
        const groups = new Map<string, IPrintDiplomasList[]>()

        for (const item of printingsList) {
            const date = item.dateEducationEnd ? parseDate(item.dateEducationEnd) : null
            const key = date ? getWeekKey(date) : '__no_date__'
            if (!groups.has(key)) groups.set(key, [])
            groups.get(key)!.push(item)
        }

        const entries = Array.from(groups.entries())
        entries.sort(([a], [b]) => {
            if (a === '__no_date__') return 1
            if (b === '__no_date__') return -1
            return b.localeCompare(a)
        })

        return entries
    }, [printingsList])

    /** Ключ текущей недели */
    const currentWeekKey = useMemo(() => getWeekKey(new Date()), [])

    /** Все ключи групп */
    const allWeekKeys = useMemo(() => weekGroups.map(([key]) => key), [weekGroups])

    /** Управляемое состояние активных панелей Collapse */
    const [activeKeys, setActiveKeys] = useState<string[]>(() =>
        weekGroups.some(([key]) => key === currentWeekKey) ? [currentWeekKey] : []
    )

    /** При изменении поиска: раскрыть все или только текущую неделю */
    useEffect(() => {
        if (txtSearch.value) {
            setActiveKeys(allWeekKeys)
        } else {
            setActiveKeys(weekGroups.some(([key]) => key === currentWeekKey) ? [currentWeekKey] : [])
        }
    }, [txtSearch.value, currentWeekKey, allWeekKeys, weekGroups])

    const footer = (
        <div className={css['footer']}>
            <Flex gap="4px" flexDirection="row" alignItems="center">
                <ToolTip title='Общее количество' placement="topLeft">
                    <p className={css['stat_all']}>{totalCount}</p>
                </ToolTip>
            </Flex>

            <Pagination
                totalCount={totalCount}
                pageSize={pageSize}
                currentPage={currentPageNumber.value}
                onChange={(e) => currentPageNumber.setValue(e)}
            />
        </div>
    )

    return <div className={css['container']}>

        <ComponentsHeader
            headerIcon={<IconPrint width="20px" height="20px" />}
            headerTitle="Работы на печать"
            txtSearch={txtSearch.value}
            txtSearchTooltip={<>Поиск по:<br />✓ Фамилии<br />✓ Имени<br />✓ Отчеству<br />✓ Телефону<br />✓ Email<br />✓ СНИЛС<br />✓ Номеру договора<br />✓ Идентификатору заявки<br />✓ Наименованию курса</>}
            txtSearchOnChange={(e) => txtSearchOnChange(e.target.value)}
            txtSearchClearOnClick={() => txtSearchOnChange('')}
            btnUpdateOnClick={() => loadDataListeners(txtSearch.value)}
        />

        <Table.Container footer={footer} footerPlacement='page'>
            <Loader isOpen={isLoading} backdrop color="var(--foreground_accent_default)" title={loadingTitle.value} />
            <Table.Header paddingLeft="52px" paddingRight="20px" classNameContainer={css['hidden_666']} classNameContent={css['table_grid_columns_header']}>
                <p style={{ textAlign: 'center' }}>№<br/>заявки</p>
                <p>ФЛ / ЮЛ / ФИО слушателя<br />Документ / Атте-я / Долг / ИАР / Тест</p>
                <p className={css['hidden_940']}>Программа<br />Тема итоговой работы</p>
                <p className={css['hidden_1200']} style={{ textAlign: 'center' }}>№ и дата<br />договора</p>
                <p className={css['hidden_1080']} style={{ textAlign: 'center' }}>Начало / конец<br />обучения</p>
                <p style={{ textAlign: 'center' }}>Действия</p>
            </Table.Header>

            <Table.Content>
                {printingsList.length === 0
                    ? <Empty image={imgEmty} title='Работы на печать отсутствуют' />
                    : (
                        <Collapse
                            activeKey={activeKeys}
                            onChange={(keys) => setActiveKeys(typeof keys === 'string' ? [keys] : keys)}
                            className={css['date_collapse']}
                            expandIconPosition="end"
                            size="small"
                        >
                            {weekGroups.map(([weekKey, items]) => {
                                const isNoDate = weekKey === '__no_date__'
                                const range = isNoDate ? null : getWeekRange(weekKey)
                                const formattedDate = isNoDate ? 'Без даты' : range ? `${formatDateShort(range.monday)} → ${formatDateShort(range.friday)}` : weekKey
                                const isCurrentWeek = weekKey === currentWeekKey

                                return (
                                    <Collapse.Panel
                                        key={weekKey}
                                        className={isCurrentWeek ? css['panel_closest'] : ''}
                                        header={
                                            <span className={css['collapse_header']}>
                                                <span className={`${css['collapse_date']} ${isCurrentWeek ? css['collapse_date_closest'] : ''}`}>{formattedDate}</span>
                                                <span className={`${css['collapse_badge']} ${isCurrentWeek ? css['collapse_badge_closest'] : ''}`}>{items.length}</span>
                                            </span>
                                        }
                                    >
                                        <Flex gap="0px" flexDirection="column">
                                            {items.map((item) => {
                                                const fullName = [item.lastName, item.firstName, item.secondName].filter(Boolean).join(' ')
                                                const siteLogo = getSiteLogo(item.dataBase)

                                                return (
                                                    <Table.Row
                                                        key={item.printID}
                                                        className={css['table_grid_columns_content']}
                                                        onClick={() => handleOpenCardPrinting(true, item.printID, fullName, item.dataBase)}
                                                    >
                                <span className={css['site_logo_cell']}>
                                    {siteLogo && <img className={css['site_logo']} src={siteLogo.src} alt={siteLogo.alt} />}
                                </span>

                                <div className={css['record_badge']} style={{ background: item.printStatusColor }}>
                                    <p>{item.applicationID}</p>
                                </div>

                                <div className={css['record_primary']}>
                                    <p>{item.contractTypeName} 👉 {fullName}</p>
                                    <p className={css['record_secondary_text']}>
                                        {item.documentTypeName} / {item.attestationTypeName} / <b>{item.diplomRegNumber || '----'}</b> / <span style={{ color: !item.diplomNumber ? '#d13232' : 'inherit' }}>{item.diplomNumber || '----'}</span>
                                    </p>
                                </div>

                                <div className={`${css['record_primary']} ${css['hidden_1040']}`.trim()}>
                                    <p>{item.courseName}</p>
                                    <p className={css['record_secondary_text']}>
                                        <span className={item.isAddressConfirmed === 1 ? css['status_ok'] : css['status_warn']}>Адрес</span> / {item.deliveryToWhom || '----'}
                                    </p>
                                </div>

                                <div className={`${css['record_dates']} ${css['hidden_900']}`.trim()}>
                                    <p>{item.contractNumber}</p>
                                    <p className={css['record_secondary_text']}>{item.contractDate}</p>
                                </div>

                                <div className={`${css['record_dates']} ${css['hidden_900']}`.trim()}>
                                    <p>{item.dateEducationStart}</p>
                                    <p className={css['record_secondary_text']}>{item.dateEducationEnd}</p>
                                </div>

                                <Flex gap="2px" flexDirection="row" alignItems="center" justifyContent="center" className={css['record_actions']}>
                                    <ToolTip title='Анкета договора' placement="bottomRight">
                                        <Button
                                            variant="icon"
                                            size={deviceType === 'mobile' ? 'xs' : 'sm'}
                                            onClick={(e) => {
                                                handleOpenCardContract(true, item.contractID)
                                                e.stopPropagation()
                                            }}
                                        >
                                            <IconContracts width={actionIconSize} height={actionIconSize} />
                                        </Button>
                                    </ToolTip>

                                    <ToolTip title='Анкета слушателя' placement="bottomRight">
                                        <Button
                                            variant="icon"
                                            size={deviceType === 'mobile' ? 'xs' : 'sm'}
                                            onClick={(e) => {
                                                handleOpenCardListener(true, item.listenerID)
                                                e.stopPropagation()
                                            }}
                                        >
                                            <IconListeners width={actionIconSize} height={actionIconSize} />
                                        </Button>
                                    </ToolTip>

                                    <ToolTip title='Удалить из раздела печати' placement="bottomRight">
                                        <Button
                                            variant="icon"
                                            size={deviceType === 'mobile' ? 'xs' : 'sm'}
                                            onClick={(e) => {
                                                handlerOpenModalConfirm(true, 'Вернуть на обработку', `Вы действительно хотите вернуть в обработку следующего слушателя:<br />👉 ${fullName};<br />👉 Договор № ${item.contractNumber}.`, 'delete', item.printID)
                                                e.stopPropagation()
                                            }}
                                        >
                                            <IconDelete width={actionIconSize} height={actionIconSize} />
                                        </Button>
                                    </ToolTip>
                                </Flex>
                            </Table.Row>
                                                )
                                            })}
                                        </Flex>
                                    </Collapse.Panel>
                                )
                            })}
                        </Collapse>
                    )
                }
            </Table.Content>
        </Table.Container>
    </div>
}
